<template>
  <div class="reporting management-page">
    <view-report-modal
      v-model="showViewModal"
      :details="showViewDetails"
      @keydown.esc="closeTransaction"
      @open-refund="handleOpenRefundFromView"
    />

    <base-modal v-model="showRefundModal">
      <refund-modal
        :transaction="transactionDetails"
        @update="handleRefundSuccess"
      />
    </base-modal>

    <base-modal v-model="showVoidModal">
      <void-modal
        :transaction="transactionDetails"
        @update="handleVoidSuccess"
      />
    </base-modal>

<!--    <div class="title" style="color: #383838; font-size: 24px">Reporting</div>-->
<!--    <div class="mt-3 mb-2 position-relative">-->
<!--      &lt;!&ndash; Add margin to separate from elements above and below &ndash;&gt;-->
<!--    </div>-->
    <!-- Server-side pagination controls -->
    <div class="row align-items-end mb-3">
      <div class="col-md-3 col-sm-6 mb-2">
        <label class="form-label" for="pageSize">Page Size</label>
        <select id="pageSize" v-model.number="pageSize" class="form-select">
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
          <option :value="250">250</option>
          <option :value="500">500</option>
          <option :value="1000">1000</option>
        </select>
      </div>
      <div class="col-md-3 col-sm-6 mb-2">
        <label class="form-label" for="pageNumber">
          {{ pageLabel }}
          <span v-if="isAllMode" class="ms-2 badge bg-info text-dark">All Results Mode</span>
        </label>
        <div class="input-group">
          <button class="btn btn-outline-secondary" :disabled="!canPrev || isAllMode || allLoading" @click="goPrev">Prev</button>
          <input id="pageNumber" v-model.number="pageNumber" type="number" min="1" class="form-control" @change="goToPage" :disabled="isAllMode || allLoading" />
          <button class="btn btn-outline-secondary" :disabled="!canNext || isAllMode || allLoading" @click="goNext">Next</button>
        </div>
      </div>
      <div class="col-md-6 col-12 mb-2 d-flex justify-content-end">
        <div class="d-flex align-items-end gap-2">
          <div v-if="hasMetaFieldSearch && metaFieldSearchTerm">
            <label for="searchParameter" class="form-label">Search Parameter</label>
            <input id="searchParameter" v-model="searchParameter" type="text" class="form-control" @keyup.enter="runSearch(true)"/>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom date range + Load All UX -->
    <div class="row g-3 align-items-end mb-3">
      <div class="col-md-3 col-sm-6">
        <label for="customStart" class="form-label">From</label>
        <input id="customStart" v-model="customStart" type="date" class="form-control" />
      </div>
      <div class="col-md-3 col-sm-6">
        <label for="customEnd" class="form-label">To</label>
        <input id="customEnd" v-model="customEnd" type="date" class="form-control" />
      </div>
      <div class="col-md-6 d-flex gap-2">
        <base-button
          class="btn btn-outline-secondary"
          :disabled="allLoading"
          @click="applyRangePaged"
        >
          Apply Range (Paged)
        </base-button>
        <base-button
          class="btn btn-outline-primary"
          :disabled="allLoading || !canLoadAll"
          @click="loadAllResults"
        >
          Load All Results
        </base-button>
        <base-button
          v-if="allLoading"
          class="btn btn-outline-danger"
          @click="cancelLoadAll"
        >
          Cancel
        </base-button>
      </div>
    </div>

    <!-- Full-screen overlay loader for All Results mode -->
    <div v-if="allLoading" class="all-results-overlay">
      <div class="overlay-content">
        <base-spinner class="overlay-spinner" />
<!--        <div class="overlay-text">Loading{{ loadingDots }}</div>-->
      </div>
    </div>
    
    <base-spinner v-if="loadingList" class="loader" />
    <AgGrid
      ref="agRef"
      :apiurl="apiUrl"
      :columns="[]"
      :define-defs="columnDefs"
      :side-bar="true"
      :pagination="false"
      :status-bar="statusBar"
      :agg-func="undefined/*aggFunc*/"
      :excel-styles="excelStyles"
      group-panel=""
      :counter="true"
      :excel-options="{ fileName: 'Transaction Report.xlsx' }"
      height="84vh"
      total-column-name="Gross Amount"
      :get-api-data-on-ready-enabled="false"
      grid-config-key="reporting-page"
      @filter-changed="filterModified"
      @row2-selected="selected => selected?.length ? openTransaction(selected[0].data) : null"
    ></AgGrid>
  </div>
</template>

<script setup>
import {ref, computed, onBeforeMount, onMounted, watch} from 'vue';
import AgGrid from '@/components/AgGrid.vue';
import BaseSpinner from '@/components/base/BaseSpinner.vue';
import ViewReportModal from '@/components/reporting/ViewReportModal.vue';
import RefundModal from '@/components/reporting/RefundModal.vue';
import VoidModal from '@/components/reporting/VoidModal.vue';
import BaseModal from '@/components/base/BaseModal.vue';
import {
  PAYMENT_METHOD_CARD, PAYMENT_METHOD_CHECK,
  PAYMENT_TYPE_ACH, PAYMENT_TYPE_ACH_READABLE, PAYMENT_TYPE_ACH_REFUND, PAYMENT_TYPE_ACH_REFUND_READABLE,
  PAYMENT_TYPE_CARD,
  PAYMENT_TYPE_CARD_READABLE,
  PERMISSION_PRIMITIVES
} from '@/config/constants';
import { useStore } from 'vuex';
import http from "@/services/http";
import cookieService from "@/services/cookies";
import BaseButton from "@/components/base/BaseButton.vue";
import { PRIMARY_CLIENT_ID } from "@/config/constants";
import { computedAsync } from '@vueuse/core';
import { shouldShowRefund, shouldShowVoid } from '@/pages/ReportingPageUtils';

const CREATED_AT_COL_ID = 'Created At';
const lastDateKey = ref(null);
let dateFetchTimer = null;

const toYMD = (d) =>
    `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

function computeUtcRangeForDates(startStr, endStr) {
  try {
    const [ys, ms, ds] = String(startStr).split('-').map(Number);
    const [ye, me, de] = String(endStr).split('-').map(Number);
    const startLocalMidnight = new Date(ys, (ms || 1) - 1, ds || 1, 0, 0, 0, 0);
    const endLocalMidnightNext = new Date(ye, (me || 1) - 1, (de || 1) + 1, 0, 0, 0, 0);
    return {
      startAtUtc: startLocalMidnight.toISOString(),
      endAtUtc: endLocalMidnightNext.toISOString(),
    };
  } catch (e) {
    return { startAtUtc: undefined, endAtUtc: undefined };
  }
}

const store = useStore();
const showLocation = computed(() => store.getters.merchantHasPermissions);
const hasPermissions = computed(() => store.getters.merchantHasPermissions);
const userHasCreditCardRefundPermission = computed(() => store.getters.grantedPermissions.includes(PERMISSION_PRIMITIVES.TRANSACTION_REFUND));
const userHasAchRefundPermission = computed(() => store.getters.grantedPermissions.includes(PERMISSION_PRIMITIVES.TRANSACTION_CREDIT_ACH_REFUND));
const userHasCreditCardRefundPermission_to_merchants = computed(() => {
  return new Set(store.state.user
    ?.account
    ?.memberships
    ?.filter(m => m?.role?.
      granted_permissions
        ?.map(e => e.name)
        ?.includes(PERMISSION_PRIMITIVES.TRANSACTION_REFUND)
    ).map(e => e.merchant.id) || []);
});
const userHasAchRefundPermission_to_merchants = computed(() => {
  return new Set(store.state.user
    ?.account
    ?.memberships
    ?.filter(m => m?.role?.
      granted_permissions
        ?.map(e => e.name)
        ?.includes(PERMISSION_PRIMITIVES.TRANSACTION_CREDIT_ACH_REFUND)
    ).map(e => e.merchant.id) || []);
});

// New permission computed properties
const userHasVoidPermission = computed(() => store.getters.grantedPermissions.includes(PERMISSION_PRIMITIVES.TRANSACTION_VOID));
const userHasVoidCardPermission = computed(() => store.getters.grantedPermissions.includes(PERMISSION_PRIMITIVES.VOID_CARD));
const userHasVoidAchPermission = computed(() => store.getters.grantedPermissions.includes(PERMISSION_PRIMITIVES.VOID_ACH));
const userHasRefundCardPermission = computed(() => store.getters.grantedPermissions.includes(PERMISSION_PRIMITIVES.REFUND_CARD));
const userHasRefundAchPermission = computed(() => store.getters.grantedPermissions.includes(PERMISSION_PRIMITIVES.REFUND_ACH));

const userHasVoidPermission_to_merchants = computed(() => {
  return new Set(store.state.user
    ?.account
    ?.memberships
    ?.filter(m => m?.role?.
      granted_permissions
        ?.map(e => e.name)
        ?.includes(PERMISSION_PRIMITIVES.TRANSACTION_VOID)
    ).map(e => e.merchant.id) || []);
});

const userHasVoidCardPermission_to_merchants = computed(() => {
  return new Set(store.state.user
    ?.account
    ?.memberships
    ?.filter(m => m?.role?.
      granted_permissions
        ?.map(e => e.name)
        ?.includes(PERMISSION_PRIMITIVES.VOID_CARD)
    ).map(e => e.merchant.id) || []);
});

const userHasVoidAchPermission_to_merchants = computed(() => {
  return new Set(store.state.user
    ?.account
    ?.memberships
    ?.filter(m => m?.role?.
      granted_permissions
        ?.map(e => e.name)
        ?.includes(PERMISSION_PRIMITIVES.VOID_ACH)
    ).map(e => e.merchant.id) || []);
});

const userHasRefundCardPermission_to_merchants = computed(() => {
  return new Set(store.state.user
    ?.account
    ?.memberships
    ?.filter(m => m?.role?.
      granted_permissions
        ?.map(e => e.name)
        ?.includes(PERMISSION_PRIMITIVES.REFUND_CARD)
    ).map(e => e.merchant.id) || []);
});

const userHasRefundAchPermission_to_merchants = computed(() => {
  return new Set(store.state.user
    ?.account
    ?.memberships
    ?.filter(m => m?.role?.
      granted_permissions
        ?.map(e => e.name)
        ?.includes(PERMISSION_PRIMITIVES.REFUND_ACH)
    ).map(e => e.merchant.id) || []);
});

const userHasAnyRefundPermission = computed(() => {
  const hasCardRefundPermissions = userHasCreditCardRefundPermission.value && userHasRefundCardPermission.value;
  const hasAchRefundPermissions = userHasAchRefundPermission.value && userHasRefundAchPermission.value;

  return hasCardRefundPermissions || hasAchRefundPermissions;
});

const userHasAnyVoidPermission = computed(() => {
  return (
    userHasVoidPermission.value &&
    (userHasVoidCardPermission.value || userHasVoidAchPermission.value)
  );
});

/**
 * Refundish actions whose display should use the original transaction
 * for payer/location/meta fields.
 */
const REFUNDISH = new Set(['refund', 'void', 'blind_refund', 'reversal', 'credit', 'terminal_void', 'terminal_refund']);

/**
 * Given a transaction row object (p.data?.[1]),
 * return the "effective" transaction that carries payer/location/meta.
 * - For charge/authorize/etc: return tx as-is.
 * - For refund/void/etc: return tx.data.api_response.original_transaction if present.
 *
 * @param {ReportTransaction | undefined | null} tx
 * @returns {ReportTransaction | undefined}
 */
function sourceTx(tx) {
  if (!tx) return tx;
  const actionMode = String(tx.action_mode || tx.action || '').toLowerCase();
  const orig = tx?.data?.api_response?.original_transaction;
  if (REFUNDISH.has(actionMode) && orig) {
    // ensure client_id is set for permission gating
    if (!orig.client_id && tx.client_id) {
      try { orig.client_id = tx.client_id; } catch (e) {
        console.error("An error has been thrown looking at sourceTx")
      }
    }
    return orig;
  }
  return tx;
}

/**
 * Safely get the effective meta object from either the tx or its original tx.
 * Falls back to {} if nothing useful is present. Handles the edge case where meta
 * is a string (e.g. "some-id") by returning {} (callers can read the string separately).
 *
 * @param {ReportTransaction | undefined} tx
 * @returns {Record<string, any> | undefined}
 */
function getEffectiveMeta(tx) {
  const direct = tx?.data?.api_response?.meta;
  const fromOrig = tx?.data?.api_response?.original_transaction?.data?.api_response?.meta;

  if (direct && typeof direct === 'object') return direct;
  if (fromOrig && typeof fromOrig === 'object') return fromOrig;
  return undefined;
}

/**
 * Safely get the effective sub_location from either the tx or its original tx.
 * @param {ReportTransaction | undefined} tx
 * @returns {{id?: string, name?: string, custom_fields?: Record<string, any>} | undefined}
 */
function getEffectiveSubLocation(tx) {
  const direct = tx?.data?.sub_location;
  const fromOrig = tx?.data?.api_response?.original_transaction?.data?.sub_location;
  return direct || fromOrig || undefined;
}

/**
 * Convenience getter for customFields under meta.
 * @param {ReportTransaction | undefined} tx
 * @returns {Record<string, any>}
 */
function getEffectiveCustomFields(tx) {
  return getEffectiveMeta(tx)?.customFields || {};
}

/**
 * Extract last-4 with multiple fallbacks:
 * - token decode (your existing path)
 * - top-level third_party_api_response.AUTH_MASKED_ACCOUNT_NBR
 * - original transaction third_party_api_response.AUTH_MASKED_ACCOUNT_NBR
 */
function getLast4(tx) {
  try {
    const token = tx?.data?.api_response?.token;
    if (token) {
      const decoded = atob(token);
      const parts = decoded.split('-');
      const lastFour = parts[2];
      if (lastFour && /^\d{3,4}$/.test(lastFour)) return lastFour.slice(-4);
    } else {
      return tx?.card_last_four;
    }
  } catch (e) {
    // fall through to masked account fallbacks
  }

  const maskedTop = tx?.data?.third_party_api_response?.AUTH_MASKED_ACCOUNT_NBR;
  if (maskedTop && maskedTop.length >= 4) return maskedTop.slice(-4);

  const maskedOrig = tx?.data?.api_response?.original_transaction?.data?.third_party_api_response?.AUTH_MASKED_ACCOUNT_NBR;
  if (maskedOrig && maskedOrig.length >= 4) return maskedOrig.slice(-4);

  return 'n/a';
}

const loadingList = ref(false);
const hasMetaFieldSearch = computed(() => store.state.user?.account?.default_client?.merchant?.configuration?.meta_field_search);
const metaFieldSearchTerm = computed(() => store.state.user?.account?.default_client?.merchant?.configuration?.meta_field_search_term);
const searchParameter = ref('');
const showViewModal = ref(false);
const showViewDetails = ref([]);
/**
 * @type {import("vue").Ref<null | ReportTransaction>}
 */
const transactionDetails = ref(null);
const showRefundModal = ref(false);
const showVoidModal = ref(false);
const excelStyles = [
  {
    id: 'dateType',
    dataType: 'DateTime', // Capitalization matters for Excel
    numberFormat: {
      format: 'yyyy-mm-dd' // Simple format for better compatibility
    }
  },
  {
    id: 'currencyStyle',
    dataType: 'Number',
    numberFormat: {
      format: '#,##0.00' // Standard currency format
    }
  },
  {
    id: 'integerStyle',
    dataType: 'Number',
    numberFormat: {
      format: '#,##0' // Integer format
    }
  },
  {
    id: 'percentStyle',
    dataType: 'Number',
    numberFormat: {
      format: '0.00%' // Percentage format
    }
  },
  {
    id: 'defaultText',
    dataType: 'String'
  },
  {
    id: 'header',
    font: {
      bold: true
    }
  }
];
// Pagination state
const DEFAULT_PAGE_SIZE = 250;
const pageNumber = ref(1);
const pageSize = ref(DEFAULT_PAGE_SIZE);
const lastPageCount = ref(0); // used to infer if a next page likely exists when total unknown
const totalCount = ref(null); // if API returns total, we will populate it
const totalPages = computed(() => {
  if (!totalCount.value || !pageSize.value) return null;
  return Math.max(1, Math.ceil(totalCount.value / pageSize.value));
});
const canPrev = computed(() => pageNumber.value > 1);
const canNext = computed(() => {
  if (totalPages.value) return pageNumber.value < totalPages.value;
  return lastPageCount.value === pageSize.value; // optimistic when total unknown
});
const pageLabel = computed(() => {
  return totalPages.value ? `Page ${pageNumber.value} of ${totalPages.value}` : 'Page';
});
// Base date range (defaults to past 7 days)
const start = ref(
  new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0]
);
const end = ref(new Date().toISOString().split('T')[0]);

// All-results mode state
const isAllMode = ref(false);
const allLoading = ref(false);
const allCancel = ref(false);
const allPage = ref(0);
const allLoadedCount = ref(0);
let requestAbortController = null;
const customStart = ref(start.value);
const customEnd = ref(end.value);
const canLoadAll = computed(() => !!customStart.value && !!customEnd.value && new Date(customStart.value) <= new Date(customEnd.value));

// start/end defined above

/**
 * @typedef {Object} ReportTransaction
 * @property {string} id - Transaction ID
 * @property {string} client_id - Client ID
 * @property {boolean} is_deleted - Whether this record is soft deleted
 * @property {string} created_at - Date of creation
 * @property {string} updated_at - Date of last update
 * @property {'settle' | 'debit' | 'blind_refund' | 'CAPTURE' | 'AUTH_CAPTURE' | 'authorize' | 'AUTH_ONLY' | 'void' | 'charge' | 'refund' | 'credit'} action - Type of action taken
 * @property {'settle' | 'debit' | 'blind_refund' | 'authorize' | 'void' | 'charge' | 'apple_pay' | 'refund'} action_mode - Category of the action
 * @property {'settle' | 'terminal_charge' | 'debit' | 'adjustment' | 'collected' | 'terminal_blind_refund' | 'cash' | 'void' | 'cc' | 'payout' | 'charge' | 'refund' | 'credit' | 'batch' | 'subscription' | 'terminal' | 'terminal_void' | 'authorize'} type - Type of transaction
 * @property {'refund' | 'cash' | 'ach' | 'card' | 'terminal'} payment_method_used - Type of payment
 * @property {boolean} is_pass_to_consumer
 * @property {boolean} request_status - Whether the request(s) to the processor was successful
 * @property {boolean} payment_status - Whether the payment was successfully processed overall
 * @property {string} currency - Type of currency used
 * @property {number | string} amount - Amount of money processed in the transaction (decimal(20, 2))
 * @property {number} fee
 * @property {number} tip
 * @property {number} surcharge
 * @property {number} platform_fee
 * @property {number} split_fee
 * @property {number} convenience_fee
 * @property {number} initial_amount
 * @property {Object.<string, string>} request_headers - Headers of original client request which initiated the transaction
 * @property {string} pro_pay_status
 * @property {Settlement} settlement
 * @property {TransactionData} data
 */

/**
 * @typedef {Object} Settlement
 * @property {string} date
 * @property {string} type - Type of settlement data source (for parsing settlement data)
 * @property {number | string} amount - Amount which was transferred as part of settlement
 * @property {Object.<string, string>} raw_data - Raw data from the data source
 */

/**
 * @typedef {Object} TransactionData
 * @property {string} id - ID of the transaction metadata
 * @property {boolean} is_deleted - Whether this record is soft deleted
 * @property {string} created_at - Date of creation
 * @property {string} updated_at - Date of last update
 * @property {string} processor - Type of third party API
 * @property {number} latency - Number of seconds waiting for response
 * @property {TransactionApiResponse} api_response - Object which was returned from the API
 * @property {Object} third_party_api_response - Object which was returned from the API from the third party API
 */

/**
 * @typedef {{
 *   amount: string
 *   convenience_fee: string
 *   details: Record<string, any>
 *   fee: string
 *   initial_amount: string
 *   ip: string
 *   method: string
 *   platform_fee: string
 *   status: boolean
 *   surcharge: string
 *   transaction_id: string
 *   meta: {
 *     customFields?: Record<string, any>
 *   } & Record<string, any>
 *   original_transaction_id: string
 *   original_transaction: {ReportTransaction}
 * }} TransactionApiResponse
 */

/**
 * this is the type of item in array returned by the API
 * @typedef {[string, ReportTransaction, string, string]} ReportingTuple
 */
const __rangeInit = computeUtcRangeForDates(start.value, end.value);
const apiUrl = ref(`/api/v1/report?start=${start.value}&end=${end.value}`
  + `${__rangeInit.startAtUtc ? `&start_at=${encodeURIComponent(__rangeInit.startAtUtc)}` : ''}`
  + `${__rangeInit.endAtUtc ? `&end_at=${encodeURIComponent(__rangeInit.endAtUtc)}` : ''}`
  + `&page=${Math.max(0, pageNumber.value - 1)}&size=${pageSize.value}`
);
/*
// no longer used since `externalFilterChanged` commented out
const filterValue = ref('year to date'.replaceAll(/ /g, ''));
*/

const defaultClientId = computed(() => store.getters.defaultClientId);

/**
 * @type {import("vue").Ref<AgGrid>}
 */
const agRef = ref(null);

const handleRefundSuccess = () => {
  showRefundModal.value = false;
  transactionDetails.value = null;
};

const handleVoidSuccess = () => {
  showVoidModal.value = false;
  transactionDetails.value = null;
  // Refresh the grid data
  if (agRef.value) {
    runSearch();
  }
};

const handleOpenRefundFromView = () => {
  if (!transactionDetails.value && showViewDetails.value?.[1]) {
    transactionDetails.value = showViewDetails.value[1];
  }

  if (!transactionDetails.value) return;

  const txData = transactionDetails.value;

  // Check if void is available
  const shouldShowVoidAction = shouldShowVoid({
    txData,
    hasPermissions,
    userHasVoidPermission,
    userHasVoidPermission_to_merchants,
    userHasVoidCardPermission,
    userHasVoidCardPermission_to_merchants,
    userHasVoidAchPermission,
    userHasVoidAchPermission_to_merchants
  });

  // Check if refund is available
  const shouldShowRefundAction = shouldShowRefund({
    txData,
    hasPermissions,
    userHasAchRefundPermission,
    userHasAchRefundPermission_to_merchants,
    userHasCreditCardRefundPermission,
    userHasCreditCardRefundPermission_to_merchants,
    userHasRefundCardPermission,
    userHasRefundCardPermission_to_merchants,
    userHasRefundAchPermission,
    userHasRefundAchPermission_to_merchants
  });

  if (shouldShowVoidAction) {
    showVoidModal.value = true;
  } else if (shouldShowRefundAction) {
    showRefundModal.value = true;
  }
};

const statusBar = {};

onMounted(async () => {
  await runSearch();
});

/*
const filterOptions = {
  5: {
    values: [
      'cc',
      'charge',
      'terminal',
      'void',
      'settle',
      'refund',
      'subscription',
      'debit',
      'credit'
    ],
    filterType: 'set'
  }
};
*/


const filterModified = (model) => {
  // If there is no Created At filter, do nothing (let client-side filters do their job)
  const createdAt = model?.[CREATED_AT_COL_ID];
  if (!createdAt?.dateFrom || !createdAt?.dateTo) return;

  // Build a stable key for the date range (LOCAL calendar days)
  const from = new Date(createdAt.dateFrom);
  const to   = new Date(createdAt.dateTo);
  const dateKey =
      `${from.getFullYear()}-${from.getMonth()}-${from.getDate()}|` +
      `${to.getFullYear()}-${to.getMonth()}-${to.getDate()}`;

  // If the date range did NOT change, do NOT refetch the server.
  // This lets users apply additional filters client-side safely.
  if (lastDateKey.value === dateKey) return;
  lastDateKey.value = dateKey;

  // Compute local-day window → UTC instants (half-open)
  const startLocalMidnight = new Date(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0, 0);
  const endLocalMidnightNextDay = new Date(to.getFullYear(), to.getMonth(), to.getDate() + 1, 0, 0, 0, 0);

  // Keep legacy YYYY-MM-DD for compatibility (not exclusive)
  start.value = toYMD(startLocalMidnight);
  end.value   = toYMD(to);
  // Keep custom range fields in sync with grid filter
  customStart.value = start.value;
  customEnd.value = end.value;

  const startAtUtc = startLocalMidnight.toISOString();
  const endAtUtc   = endLocalMidnightNextDay.toISOString();

  // Reset to first page when date range changes
  pageNumber.value = 1;
  const url = `/api/v1/report?start=${start.value}&end=${end.value}` +
      `&start_at=${encodeURIComponent(startAtUtc)}` +
      `&end_at=${encodeURIComponent(endAtUtc)}` +
      `&page=${Math.max(0, pageNumber.value - 1)}` +
      `&size=${pageSize.value}`;

  apiUrl.value = url;

  if (!agRef.value) return;

  // IMPORTANT: do NOT clear rows anymore — that’s what nuked the Set Filter options
  // Optional: debounce a bit to swallow rapid consecutive date changes
  clearTimeout(dateFetchTimer);
  dateFetchTimer = setTimeout(() => {
    // If we are in All-Results mode, re-run the full loader
    if (isAllMode.value) {
      loadAllResults();
    } else {
      runSearch(true);
    }
  }, 200);
};

// Helper: try to read contract array from a row or object
function tryExtractContractFromRow(rowOrObj) {
  try {
    if (!rowOrObj) return null;
    // If already an object with metadata_contract
    if (!Array.isArray(rowOrObj) && typeof rowOrObj === 'object') {
      const mc = rowOrObj.metadata_contract;
      if (Array.isArray(mc)) return mc; // legacy shape
      if (mc && typeof mc === 'object' && Array.isArray(mc.keys)) return mc.keys; // new shape { keys: [...] }
    }
    // If it's a row (array), search any element that is an object with metadata_contract
    if (Array.isArray(rowOrObj)) {
      for (const item of rowOrObj) {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const mc = item.metadata_contract;
          if (Array.isArray(mc)) return mc; // legacy shape
          if (mc && typeof mc === 'object' && Array.isArray(mc.keys)) return mc.keys; // new shape
        }
      }
    }
  } catch (_) { /* ignore */ }
  return null;
}

// Extract metadata keys using server-provided metadata_contract when present; fallback to first row meta
const firstRowMetaKeys = (transactions) => {
  if (!Array.isArray(transactions) || !transactions.length) return [];

  // 1) Prefer explicit contract from backend (can be on first row, any row, or a standalone object)
  let contract = tryExtractContractFromRow(transactions[0]);
  if (!contract) {
    for (const t of transactions) {
      contract = tryExtractContractFromRow(t);
      if (contract) break;
    }
  }
  if (Array.isArray(contract) && contract.length) {
    return contract.map(k => String(k));
  }

  // 2) Fallback: infer from first row's meta object
  const first = transactions[0]?.[1];
  const meta = getEffectiveMeta(sourceTx(first));
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return [];
  return Object.keys(meta);
};

const firstRowCustomFieldKeys = (transactions) => {
  if (!Array.isArray(transactions) || !transactions.length) return [];
  const first = transactions[0]?.[1];
  const cf = getEffectiveCustomFields(sourceTx(first)) || {};
  if (!cf || typeof cf !== 'object' || Array.isArray(cf)) return [];
  return Object.keys(cf);
};

// Extract metadata keys by iterating every row's meta and unioning keys
const allRowsMetaKeys = (transactions) => {
  if (!Array.isArray(transactions) || !transactions.length) return [];
  const keys = new Set();
  const rows = transactions.filter(Array.isArray);
  for (const row of rows) {
    const tx = sourceTx(row?.[1]);
    const meta = getEffectiveMeta(tx);
    if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
      for (const k of Object.keys(meta)) keys.add(k);
    }
  }
  // If nothing found, optional fallback to any explicit contract embedded
  if (keys.size === 0) {
    const contract = tryExtractContractFromRow(transactions[0]) || transactions.reduce((acc, t) => acc || tryExtractContractFromRow(t), null);
    if (Array.isArray(contract)) contract.forEach(k => keys.add(String(k)));
  }
  return Array.from(keys);
};

// Extract custom field keys by union across all rows
const allRowsCustomFieldKeys = (transactions) => {
  if (!Array.isArray(transactions) || !transactions.length) return [];
  const keys = new Set();
  const rows = transactions.filter(Array.isArray);
  for (const row of rows) {
    const tx = sourceTx(row?.[1]);
    const cf = getEffectiveCustomFields(tx) || {};
    if (cf && typeof cf === 'object' && !Array.isArray(cf)) {
      for (const k of Object.keys(cf)) keys.add(k);
    }
  }
  return Array.from(keys);
};

/**
 * @typedef {{
 *   id: string
 *   merchant: {id: string}
 *   customizations: Record<string, *>
 * }} ReportingPageMyConfiguration
 */

/**
 * @type {import("vue").ComputedRef<Array<ReportingPageMyConfiguration>>}
 */
const myConfigurations = computedAsync(async () => {
  let response = await http.get("/api/client/my-configurations");
  return response.data.message;
}, []);

const runSearchDisabled = ref(false);
async function runSearch(resetToFirstPage = false) {
  loadingList.value = true;
  // runSearchDisabled.value = true;
  const param = searchParameter.value;

  try {
    // Ensure only one request at a time
    try { requestAbortController?.abort(); } catch (_) {
      console.log("issue with abort controller")
    }
    requestAbortController = new AbortController();

    let theApiUrl = '';
    if (resetToFirstPage) pageNumber.value = 1;
    if (param) {
      theApiUrl = `/api/v1/report?start=${start.value}&end=${end.value}&search=${searchParameter.value}=${param}`;
    } else {
      theApiUrl = `/api/v1/report?start=${start.value}&end=${end.value}`;
    }
    // include local-day UTC window
    const { startAtUtc, endAtUtc } = computeUtcRangeForDates(start.value, end.value);
    if (startAtUtc) theApiUrl += `&start_at=${encodeURIComponent(startAtUtc)}`;
    if (endAtUtc) theApiUrl += `&end_at=${encodeURIComponent(endAtUtc)}`;
    // include pagination
    theApiUrl += `&page=${Math.max(0, pageNumber.value - 1)}&size=${pageSize.value}`;

    const skipDynamicMeta = defaultClientId.value === PRIMARY_CLIENT_ID;

    // Fetch data
    const response = await http.get(theApiUrl, { signal: requestAbortController.signal });
    // Support either array message or object with data/items
    const message = response?.data?.message ?? response?.data ?? null;
    const transactions = Array.isArray(message)
      ? message
      : (Array.isArray(message?.data)
        ? message.data
        : (Array.isArray(message?.items)
          ? message.items
          : (Array.isArray(response?.data?.data) ? response.data.data : [])));

    // Try to capture total count if provided
    totalCount.value = (
        response?.data?.total ?? null
    ) ?? null;
    // Filter out any non-array items (e.g., standalone contract objects)
    const arrayRows = Array.isArray(transactions) ? transactions.filter(Array.isArray) : [];
    lastPageCount.value = arrayRows.length;
    apiUrl.value = theApiUrl; // keep current url in sync for refreshAPI callers
    isAllMode.value = false; // we are in paged mode after an explicit search

    if (arrayRows.length > 0) {

      columnDefsExtra.value = [];
      if (!skipDynamicMeta) {
        // Meta columns from union of all rows' meta
        const metaKeys = allRowsMetaKeys(arrayRows);
        const metaColumns = createMetaColumns(metaKeys);
        if (metaColumns.length) {
          const metaValues = { headerName: 'Meta', children: metaColumns };
          metaColumns.push({
            headerName: 'Meta ID',
            colId: 'Meta ID',
            filter: 'agTextColumnFilter',
            valueGetter: (p) => {
              const row = p?.data?.[1];
              if (!row) return '';
              const directMeta = row?.data?.api_response?.meta;
              if (typeof directMeta === 'string') return directMeta;
              const origMeta = row?.data?.api_response?.original_transaction?.data?.api_response?.meta;
              if (typeof origMeta === 'string') return origMeta;
              return '';
            },
          });
          columnDefsExtra.value.push(metaValues);
        }

        // Custom field columns from union of all rows' customFields
        const customKeys = allRowsCustomFieldKeys(arrayRows);
        const customCols = createCustomFieldColumns(customKeys);
        if (customCols.length) {
          columnDefsExtra.value.push({ headerName: 'Custom Fields', children: customCols });
        }
      }
      // Update ag-grid rows and columns
      if (agRef.value) {
        agRef.value.setTheRowData(arrayRows);  // Set the row data
        agRef.value.setTheColumnDefs(columnDefs.value);  // Update column definitions
        try { agRef.value?.computeTotals?.(); } catch (_) {console.error("error with compute totals")}
      }
    } else {
      // No rows: clear grid
      if (agRef.value) {
        agRef.value.setTheRowData([]);
      }
    }
  } catch (error) {
    if (error?.name === 'CanceledError' || error?.message === 'canceled') {
      // silent on cancel
    } else {
      console.error('Error fetching data:', error);
    }
  } finally {
    loadingList.value = false;
    // runSearchDisabled.value = false;
  }
}

function goPrev() {
  if (!canPrev.value) return;
  pageNumber.value -= 1;
  runSearch();
}

function goNext() {
  if (!canNext.value) return;
  pageNumber.value += 1;
  runSearch();
}

function goToPage() {
  if (!pageNumber.value || pageNumber.value < 1) pageNumber.value = 1;
  if (totalPages.value && pageNumber.value > totalPages.value) pageNumber.value = totalPages.value;
  runSearch();
}

// react to page size changes
watch(pageSize, (newVal, oldVal) => {
  if (!newVal || newVal <= 0) {
    pageSize.value = DEFAULT_PAGE_SIZE;
    return;
  }
  pageNumber.value = 1; // reset to first page on size change
  if (isAllMode.value) {
    loadAllResults();
  } else {
    runSearch();
  }
});

function applyRangePaged() {
  // apply customStart/customEnd to server variables and re-run paged search
  if (!canLoadAll.value) return;
  start.value = customStart.value;
  end.value = customEnd.value;
  pageNumber.value = 1;
  isAllMode.value = false;
  runSearch(true);
}

async function loadAllResults() {
  if (!canLoadAll.value) return;
  isAllMode.value = true;
  allLoading.value = true;
  allCancel.value = false;
  allPage.value = 0;
  allLoadedCount.value = 0;
  startDots();

  // Cancel any inflight request
  try { requestAbortController?.abort(); } catch (_) { /* noop */ }
  requestAbortController = new AbortController();

  // Set server params from custom range
  start.value = customStart.value;
  end.value = customEnd.value;

  const param = searchParameter.value;
  let url = `/api/v1/report?start=${start.value}&end=${end.value}`;
  if (param) url += `&search=${searchParameter.value}=${param}`;
  const { startAtUtc, endAtUtc } = computeUtcRangeForDates(start.value, end.value);
  if (startAtUtc) url += `&start_at=${encodeURIComponent(startAtUtc)}`;
  if (endAtUtc) url += `&end_at=${encodeURIComponent(endAtUtc)}`;
  // Hint backend to stream NDJSON for this portal request
  url += `&is_portal_request=true`;

  try {
    // Try NDJSON streaming first; fall back to JSON
    const streamed = await loadAllResultsNdjson(url, requestAbortController.signal);
    if (streamed) {
      return;
    }
    const resp = await http.get(url, { signal: requestAbortController.signal });
    const msg = resp?.data?.message ?? resp?.data ?? null;
    const combined = Array.isArray(msg)
      ? msg
      : (Array.isArray(msg?.data)
        ? msg.data
        : (Array.isArray(msg?.items)
          ? msg.items
          : (Array.isArray(resp?.data?.data) ? resp.data.data : [])));

    if (allCancel.value) return;

    // Filter out any non-array items (e.g., standalone contract objects)
    const arrayRows = Array.isArray(combined) ? combined.filter(Array.isArray) : [];
    lastPageCount.value = arrayRows.length;
    pageNumber.value = 1;
    if (agRef.value) {
      const skipDynamicMeta = defaultClientId.value === PRIMARY_CLIENT_ID;
      const metaKeys = skipDynamicMeta ? [] : allRowsMetaKeys(arrayRows);
      const metaColumns = skipDynamicMeta ? [] : createMetaColumns(metaKeys);
      const customKeys = skipDynamicMeta ? [] : allRowsCustomFieldKeys(arrayRows);
      const customCols = skipDynamicMeta ? [] : createCustomFieldColumns(customKeys);

      columnDefsExtra.value = [];
      if (metaColumns.length) {
        const metaValues = { headerName: 'Meta', children: metaColumns };
        metaColumns.push({
          headerName: 'Meta ID',
          colId: 'Meta ID',
          filter: 'agTextColumnFilter',
          valueGetter: (p) => {
            const row = p?.data?.[1];
            if (!row) return '';
            const directMeta = row?.data?.api_response?.meta;
            if (typeof directMeta === 'string') return directMeta;
            const origMeta = row?.data?.api_response?.original_transaction?.data?.api_response?.meta;
            if (typeof origMeta === 'string') return origMeta;
            return '';
          },
        });
        columnDefsExtra.value.push(metaValues);
      }
      if (customCols.length) columnDefsExtra.value.push({ headerName: 'Custom Fields', children: customCols });

      agRef.value.setTheRowData(arrayRows);
      agRef.value.setTheColumnDefs(columnDefs.value);
      try { agRef.value?.computeTotals?.(); } catch (_) {console.error("error with computeTotals")}
    }
  } catch (e) {
    if (e?.name === 'CanceledError' || e?.message === 'canceled') {
      // ignore cancel
    } else {
      console.error('Error loading all results:', e);
    }
  } finally {
    stopDots();
    allLoading.value = false;
    try { requestAbortController = null; } catch (_) { /* noop */ }
  }
}

function cancelLoadAll() {
  allCancel.value = true;
  try { requestAbortController?.abort(); } catch (_) { /* noop */ }
}

// NDJSON streaming loader: returns true if handled via streaming, false to fall back
async function loadAllResultsNdjson(baseUrl, signal) {
  try {
    const token = cookieService.getIdentityToken();
    const headers = {
      Accept: 'application/x-ndjson',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const resp = await fetch(baseUrl, {
      headers,
      signal,
      cache: 'no-store',
    });
    const ct = resp.headers.get('content-type') || '';
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    if (!ct.includes('ndjson') && !ct.includes('x-ndjson')) {
      return false; // server not streaming NDJSON; fall back to normal JSON
    }

    // Prepare grid for streaming results
    if (agRef.value) {
      agRef.value.setTheRowData([]); // clear existing
    }

    const reader = resp.body?.getReader?.();
    if (!reader) return false;

    const decoder = new TextDecoder();
    let buffer = '';
    let firstRow = null;
    let ndjsonContractKeys = null; // capture contract sent as separate object line
    // Dynamic discovery of meta/custom keys across all streamed rows
    const discoveredMetaKeys = new Set();
    const discoveredCustomKeys = new Set();
    let lastMetaCount = 0;
    let lastCustomCount = 0;
    let batch = [];
    const skipDynamicMeta = defaultClientId.value === PRIMARY_CLIENT_ID;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let idx;
      while ((idx = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, idx).trim();
        buffer = buffer.slice(idx + 1);
        if (!line) continue;
        let parsed;
        try { parsed = JSON.parse(line); } catch (_) { continue; }

        // If line is an object, see if it carries metadata_contract info
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const mc = parsed.metadata_contract;
          if (Array.isArray(mc)) ndjsonContractKeys = mc;
          else if (mc && typeof mc === 'object' && Array.isArray(mc.keys)) ndjsonContractKeys = mc.keys;
          continue; // not a row
        }

        // Expect each line to be a single report row (array form)
        const normalized = Array.isArray(parsed) ? parsed : null;
        if (!normalized) continue;

        if (!firstRow) {
          firstRow = normalized;
          // do not set columns yet; we will build/update as keys are discovered
          if (agRef.value && skipDynamicMeta) agRef.value.setTheColumnDefs(columnDefs.value);
        }

        // Discover keys for this row
        if (!skipDynamicMeta) {
          const tx = sourceTx(normalized?.[1]);
          const meta = getEffectiveMeta(tx);
          if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
            for (const k of Object.keys(meta)) discoveredMetaKeys.add(k);
          }
          const cf = getEffectiveCustomFields(tx);
          if (cf && typeof cf === 'object' && !Array.isArray(cf)) {
            for (const k of Object.keys(cf)) discoveredCustomKeys.add(k);
          }

          // Also merge in any prior contract keys (if provided separately)
          if (Array.isArray(ndjsonContractKeys)) {
            for (const k of ndjsonContractKeys) discoveredMetaKeys.add(String(k));
          }

          const metaCount = discoveredMetaKeys.size;
          const customCount = discoveredCustomKeys.size;
          const keysChanged = (metaCount !== lastMetaCount) || (customCount !== lastCustomCount);
          if (keysChanged && agRef.value) {
            lastMetaCount = metaCount;
            lastCustomCount = customCount;

            const metaColumns = createMetaColumns(Array.from(discoveredMetaKeys));
            const customCols = createCustomFieldColumns(Array.from(discoveredCustomKeys));
            columnDefsExtra.value = [];
            if (metaColumns.length) {
              const metaValues = { headerName: 'Meta', children: metaColumns };
              metaColumns.push({
                headerName: 'Meta ID',
                colId: 'Meta ID',
                filter: 'agTextColumnFilter',
                valueGetter: (p) => {
                  const r = p?.data?.[1];
                  if (!r) return '';
                  const dMeta = r?.data?.api_response?.meta;
                  if (typeof dMeta === 'string') return dMeta;
                  const oMeta = r?.data?.api_response?.original_transaction?.data?.api_response?.meta;
                  if (typeof oMeta === 'string') return oMeta;
                  return '';
                },
              });
              columnDefsExtra.value.push(metaValues);
            }
            if (customCols.length) columnDefsExtra.value.push({ headerName: 'Custom Fields', children: customCols });
            agRef.value.setTheColumnDefs(columnDefs.value);
          }
        }

        batch.push(normalized);
        allLoadedCount.value += 1;
      }
    }

    // Flush any remaining buffered line (no trailing newline)
    const leftover = buffer.trim();
    if (leftover) {
      try {
        const row = JSON.parse(leftover);
        if (Array.isArray(row)) {
          batch.push(row);
        }
      } catch (_) { /* ignore */ }
    }
    if (agRef.value) {
      agRef.value.setTheRowData(batch);
    }

    // Recompute totals/avg/count after full stream completes
    try { agRef.value?.computeTotals?.(); } catch (_) {console.error("error with totals/avg/count in computeTotals")}

    return true;
  } catch (err) {
    if (err?.name === 'AbortError') return true; // handled cancel via streaming path
    console.error('NDJSON stream error:', err);
    return false;
  }
}

// Simple animated dots for "Loading..."
const loadingDots = ref('');
let loadingDotsInterval = null;
function startDots() {
  stopDots();
  loadingDots.value = '';
  loadingDotsInterval = setInterval(() => {
    loadingDots.value = loadingDots.value.length >= 3 ? '' : loadingDots.value + '.';
  }, 1000);
}
function stopDots() {
  if (loadingDotsInterval) {
    clearInterval(loadingDotsInterval);
    loadingDotsInterval = null;
  }
  loadingDots.value = '';
}

// Function to dynamically create meta columns
const createMetaColumns = (metaKeys) => {
  const columns = [];
  metaKeys.forEach(key => {
    // Exclude some known PII or redundant fields
    if (!['email', 'firstName', 'lastName', 'first_name', 'last_name', 'customFields'].includes(key)) {
      columns.push({
        headerName: key.charAt(0).toUpperCase() + key.slice(1),
        filter: 'agTextColumnFilter',
        valueGetter: (p) => {
          const tx = sourceTx(p?.data?.[1]);
          const meta = getEffectiveMeta(tx) || {};
          return meta?.[key] ?? '';
        },
      });
    }
  });
  return columns;
};


const createCustomFieldColumns = (customFieldKeys) => {
  const columns = [];
  customFieldKeys.forEach(key => {
    columns.push({
      headerName: key.charAt(0).toUpperCase() + key.slice(1),
      filter: 'agTextColumnFilter',
      valueGetter: (p) => {
        const tx = sourceTx(p?.data?.[1]);
        const value = getEffectiveCustomFields(tx)?.[key] ?? '';
        // You can reintroduce permission gating here if desired:
        // const membership = store.getters.membershipsByMerchant[tx?.client_id];
        // const allowed = key in (membership?.role?.custom_field_access || {});
        // return allowed ? value : '';
        return value;
      }
    });
  });
  return columns;
};

/*
const externalFilterChanged = () => {
  const currentDate = new Date();
  const today = new Date().toISOString().split('T')[0];
  switch (filterValue.value) {
    case 'past7days':
      start.value = new Date(currentDate.getTime() - 8 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0];
      end.value = today;
      break;
    case 'past30days':
      start.value = new Date(currentDate.getTime() - 31 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0];
      end.value = today;
      break;
    case 'yeartodate':
      start.value = new Date(currentDate.getFullYear(), 0, 1)
        .toISOString()
        .split('T')[0];
      end.value = today;
      break;
    case 'lastmonth':
      start.value = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - 1,
        1
      )
        .toISOString()
        .split('T')[0];
      end.value = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0)
        .toISOString()
        .split('T')[0];
      break;
    case 'monthtodate':
      start.value = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      )
        .toISOString()
        .split('T')[0];
      end.value = today;
      break;
    default:
      start.value = new Date(currentDate.getTime() - 8 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0];
      end.value = today;
  }
  apiUrl.value = `/api/v1/report?&start=${start.value}&end=${end.value}`;
  agRef.value.refreshAPI(`/api/v1/report?&start=${start.value}&end=${end.value}`);
};
*/

/**
 * @typedef {import("ag-grid-community").ColDef | import("ag-grid-community").ColGroupDef} AgColumn
 */

/**
 * @type {import("vue").Ref<Array<AgColumn>>}
 */
const columnDefsExtra = ref([]);

/**
 * @type {import("vue").Ref<Array<AgColumn>>}
 */
// noinspection JSUnusedGlobalSymbols
const columnDefs = computed(() => [
  {
    headerName: `Transaction Details`,
    children: [
      {
        headerName: 'ID',
        colId: 'ID',
        filter: 'agTextColumnFilter',
        valueGetter: (p) => {
          if (!p.data) return '';
          return p.data[1]?.id;
        },
      },
      {
        headerName: 'Created At',
        colId: 'Created At',
        cellClass: 'dateType',
        valueGetter: (p) => {
          if (!p.data) return null;
          const iso = p.data[0];
          return iso ? new Date(iso) : null;  // keep full timestamp for display/sort
        },
        valueFormatter: (p) => {
          if (!p.value) return '';
          try {
            return new Date(p.value).toLocaleString('en-US');
          } catch {
            return '';
          }
        },
        filter: 'agDateColumnFilter',
        filterParams: {
          filterOptions: ['inRange'],
          inRangeInclusive: true,
          // >>> KEY BIT: compare by date only (strip time)
          comparator: (filterLocalDateAtMidnight, cellValue) => {
            if (!cellValue) return -1;
            const d = cellValue instanceof Date ? cellValue : new Date(cellValue);
            const cellNoTime = new Date(d.getFullYear(), d.getMonth(), d.getDate()); // midnight
            if (cellNoTime < filterLocalDateAtMidnight) return -1;
            if (cellNoTime > filterLocalDateAtMidnight) return 1;
            return 0; // same date
          }
        },
        sort: 'desc'
      },
      {
        headerName: 'Parent',
        colId: 'Parent',
        filter: 'agSetColumnFilter',
        valueGetter: (p) => {
          if (!p.data) return '';
          return p.data[3];
        }
      },
      {
        headerName: 'DBA Name',
        colId: 'DBA Name',
        filter: 'agSetColumnFilter',
        valueGetter: (p) => {
          if (!p.data) return '';
          return p.data[2];
        }
      },
      ...((!showLocation.value) ? [] : [{
        headerName: 'Location',
        colId: 'Location',
        filter: 'agSetColumnFilter',
        valueGetter: (p) => {
          const tx = sourceTx(p?.data?.[1]);
          return getEffectiveSubLocation(tx)?.name || '';
        },
      }, {
        headerName: 'RD',
        colId: 'rd',
        filter: 'agTextColumnFilter',
        valueGetter: (p) => {
          const tx = sourceTx(p?.data?.[1]);
          return getEffectiveSubLocation(tx)?.custom_fields?.RD || '';
        }
      }, {
        headerName: 'VPCO',
        colId: 'vpco',
        filter: 'agTextColumnFilter',
        valueGetter: (p) => {
          const tx = sourceTx(p?.data?.[1]);
          return getEffectiveSubLocation(tx)?.custom_fields?.VPCO || '';
        }
      }, {
        headerName: 'Sub-Account Number',
        colId: 'subAccountNumber',
        filter: 'agTextColumnFilter',
        valueGetter: (p) => {
          const tx = sourceTx(p?.data?.[1]);
          return getEffectiveSubLocation(tx)?.custom_fields?.['Sub-Account Number'] || '';
        }
      }]),
      {
        headerName: 'Payment Method',
        colId: 'Payment Method',
        filter: 'agSetColumnFilter',
        valueGetter: (p) => {
          if (!p.data) return '';
          const theType = p.data[1]?.type;
          if (theType === PAYMENT_TYPE_CARD) {
            return PAYMENT_METHOD_CARD;
          }
          if (theType === PAYMENT_TYPE_ACH) {
            return PAYMENT_METHOD_CHECK;
          }
          if (theType === PAYMENT_TYPE_ACH_REFUND) {
            return PAYMENT_METHOD_CHECK;
          }
          return theType;
        }
      },
      {
        headerName: 'Transaction Type',
        colId: 'Transaction Type',
        filter: 'agSetColumnFilter',
        valueGetter: (p) => {
          if (!p.data) return '';
          const theAction = p.data[1]?.action;
          if (theAction === PAYMENT_TYPE_ACH) {
            return PAYMENT_TYPE_ACH_READABLE;
          }
          if (theAction === PAYMENT_TYPE_ACH_REFUND) {
            return PAYMENT_TYPE_ACH_REFUND_READABLE;
          }
          return theAction;
        }
      },
      {
        headerName: 'Original Transaction',
        colId: 'Original Transaction',
        filter: 'agTextColumnFilter',
        valueGetter: (p) => {
          return p.data?.[1]?.data?.api_response?.original_transaction_id || '';
        },
        cellRenderer: (p) => {
          const tx = p?.data?.[1];
          const oTid = tx?.data?.api_response?.original_transaction_id;
          if (!oTid) return '';
          const label = (tx?.action_mode || tx?.action || '').toLowerCase().includes('void') ? 'Void' : 'Refund';
          return `<p style="font-size: 12px; text-decoration: underline;">${label} for ${oTid}</p>`;
        },
        onCellClicked: function (params) {
          let value = params.node.data;
          let dataIsValid = Array.isArray(value) && value.length === 4 && value[1]?.data?.api_response?.original_transaction;
          if (!dataIsValid) return;
          let txData = value[1]?.data?.api_response?.original_transaction;
          try {
            txData.client_id = txData.client_id || value[1].client_id;
          } catch(e) {
            //
          }

          // Check both void and refund eligibility for original transaction
          const shouldShowVoidAction = shouldShowVoid({
            txData,
            hasPermissions,
            userHasVoidPermission,
            userHasVoidPermission_to_merchants,
            userHasVoidCardPermission,
            userHasVoidCardPermission_to_merchants,
            userHasVoidAchPermission,
            userHasVoidAchPermission_to_merchants
          });

          const shouldShowRefundAction = shouldShowRefund({
            txData,
            hasPermissions,
            userHasAchRefundPermission,
            userHasAchRefundPermission_to_merchants,
            userHasCreditCardRefundPermission,
            userHasCreditCardRefundPermission_to_merchants,
            userHasRefundCardPermission,
            userHasRefundCardPermission_to_merchants,
            userHasRefundAchPermission,
            userHasRefundAchPermission_to_merchants
          });

          if (shouldShowVoidAction || shouldShowRefundAction) {
            showViewDetails.value = [value[0], txData, value[2], value[3]];
            showViewModal.value = true;
          }
        }
      },
      {
        headerName: 'Authorized',
        colId: 'Authorized',
        filter: 'agSetColumnFilter',
        valueGetter: (p) => {
          if (!p.data) return '';
          return p.data[1]?.payment_status;
        },
        cellStyle: function (p) {
          if (!p.data) return '';
          // noinspection EqualityComparisonWithCoercionJS
          if (
            p.data[1]?.payment_status == false ||
            p.data[1]?.payment_status == 'false' ||
            p.data[1]?.payment_status == 'False'
          ) {
            //Here you can check the value and based on that you can change the color
            //mark police cells as red
            return {color: '#a87e71'};
          } else {
            return {color: '#86a871'};
          }
        }
      },
      {
        headerName: 'Status',
        colId: 'Status',
        filter: 'agSetColumnFilter',
        valueGetter: (p) => {
          if (!p.data) return '';
          return p.data[1]?.status;
        },
      },
      {
        headerName: 'Last 4',
        colId: 'Last 4',
        filter: 'agSetColumnFilter',
        valueGetter: (p) => {
          if (!p?.data) return '';
          return getLast4(p.data[1]);
        },
      },
      {
        headerName: 'Gross Amount',
        colId: 'Gross Amount',
        filter: 'agNumberColumnFilter',
        field: 'amount',
        aggFunc: 'total',
        cellClass: 'currencyStyle',
        // Keep calculation logic but ensure numeric return type
        valueGetter: (p) => {
          if (!p.data) return null;

          const theData = p.data[1];
          const theProPayStatus = theData?.pro_pay_status || null;
          const typesToChargeProPayFeeTo = [
            'ACHInReturned', 'ACHInRejected',
          ];

          if (theProPayStatus !== null && typesToChargeProPayFeeTo.includes(theProPayStatus)) {
            return -25.00; // Return numeric value
          }

          const theStatus = theData?.payment_status || "False";
          if (theStatus === "False") {
            return null; // Return null, not string
          }

          const theAction = theData?.action_mode || null;
          if (theAction === null) {
            return null;
          }

          const typesToTotal = [
            "settle", "debit", "charge", "apple_pay"
          ];

          const typesToSubtract = [
            "refund", "void", "blind_refund", "reversal", "credit"
          ];

          const theAmount = parseFloat(theData?.amount) || 0.0;

          if (typesToSubtract.includes(theAction)) {
            return -theAmount; // Return numeric
          }

          if (!typesToTotal.includes(theAction)) {
            return null;
          }

          return theAmount; // Return numeric
        },
        // Format for display only, not affecting export
        valueFormatter: (p) => {
          if (p.value === null || p.value === undefined) return '';
          try {
            // For display only
            return parseFloat(p.value).toFixed(2);
          } catch (e) {
            return '0.00';
          }
        }
      },
      {
        headerName: 'Card Brand',
        colId: 'Card Brand',
        filter: 'agTextColumnFilter',
        valueGetter: (p) => {
          if (!p.data) return 'n/a';
          return p.data[1]?.card_brand;
        },
      },
      {
        headerName: 'Card Type',
        colId: 'Card Type',
        filter: 'agTextColumnFilter',
        valueGetter: (p) => {
          if (!p.data) return 'n/a';
          return p.data[1]?.card_type;
        },
      },
      {
        headerName: 'Settlement',
        colId: 'Settlement',
        filter: 'agNumberColumnFilter',
        cellClass: 'currencyStyle',
        valueGetter: p => {
          if (!p.data) return null;

          const theData = p.data[1];
          const theProPayStatus = theData?.pro_pay_status || null;
          const typesForProPayTransactionThatDoNotSettle = [
            'ACHInReturned', 'ACHInRejected', 'CCDebitVoided'
          ];

          if (theProPayStatus !== null && typesForProPayTransactionThatDoNotSettle.includes(theProPayStatus)) {
            return null; // Return null, not 'n/a'
          }

          const settlementAmount = p.data[1]?.settlement?.amount;
          return settlementAmount ? parseFloat(settlementAmount) : null; // Return numeric, not string
        },
        // Format for display only
        valueFormatter: p => {
          if (p.value === null || p.value === undefined) {
            // Check if we should display 'n/a'
            if (p.data && p.data[1]?.pro_pay_status) {
              const theProPayStatus = p.data[1].pro_pay_status;
              const typesForProPayTransactionThatDoNotSettle = [
                'ACHInReturned', 'ACHInRejected', 'CCDebitVoided'
              ];
              if (typesForProPayTransactionThatDoNotSettle.includes(theProPayStatus)) {
                return 'n/a';
              }
            }
            return '';
          }

          try {
            return parseFloat(p.value).toFixed(2);
          } catch (e) {
            return '';
          }
        }
      },
      // Fix for Settlement Date column
      {
        headerName: 'Settlement Date',
        colId: 'Settlement Date',
        cellClass: 'dateType',
        valueGetter: (p) => {
          if (!p.data || !p.data[1]?.settlement?.date) return null;

          const theData = p.data[1];
          const theProPayStatus = theData?.pro_pay_status || null;
          const typesForProPayTransactionThatDoNotSettle = [
            'ACHInReturned', 'ACHInRejected', 'CCDebitVoided'
          ];

          if (theProPayStatus !== null && typesForProPayTransactionThatDoNotSettle.includes(theProPayStatus)) {
            return null; // Return null instead of 'n/a' for export
          }

          try {
            // Return a proper Date object for Excel
            return new Date(p.data[1].settlement.date);
          } catch (e) {
            return null;
          }
        },
        valueFormatter: (p) => {
          if (!p.value) {
            // Custom formatting for display only
            if (p.data && p.data[1]?.pro_pay_status) {
              const theProPayStatus = p.data[1].pro_pay_status;
              const typesForProPayTransactionThatDoNotSettle = [
                'ACHInReturned', 'ACHInRejected', 'CCDebitVoided'
              ];
              if (typesForProPayTransactionThatDoNotSettle.includes(theProPayStatus)) {
                return 'n/a';
              }
            }
            return '';
          }

          try {
            // Format for display
            return p.value.toLocaleDateString('en-US');
          } catch (e) {
            return '';
          }
        }
      },
    ]
  },
  ...(!store.getters.hasPermission(PERMISSION_PRIMITIVES.REPORTS_ADVANCED) ? [] : [{
    headerName: 'Advanced Transaction Details',
    children: [
      {
        headerName: 'Net Amount',
        colId: 'Net Amount',
        filter: 'agNumberColumnFilter',
        cellClass: 'currencyStyle',
        field: 'amount',
        /**
         * @param {{data: ReportingTuple}} p
         */
        valueGetter: (p) => {
          if (!p.data) return null;
          // oh my god
          const theData = p.data[1];

          const theProPayStatus = theData?.pro_pay_status || null;

          const typesToChargeProPayFeeTo = [
            'ACHInReturned', 'ACHInRejected',
          ];

          if (theProPayStatus !== null && typesToChargeProPayFeeTo.includes(theProPayStatus)) {
            return -25.00;
          }

          const theStatus = theData?.payment_status || "False";
          if (theStatus === "False") {
            return null;
          }

          const theAction = theData?.action_mode || null;
          if (theAction === null) {
            return null;
          }

          const typesToTotal = [
            "settle", "debit", "charge", "apple_pay"
          ];

          const typesToSubtract = [
            "refund", "void", "blind_refund", "reversal", "credit"
          ];

          const theFee = parseFloat(theData?.fee) || 0.0;
          const theAmount = parseFloat(theData?.amount) || 0.0;

          if (typesToSubtract.includes(theAction)) {
            return -theAmount;
          }

          if (!typesToTotal.includes(theAction)) {
            return null;
          }

          return theAmount - theFee;
        },
        valueFormatter: (p) => {
          if (!p.data) return null;
          // do not use the value from backend, use value as computed above
          // return parseFloat(p.data[1]?.amount).toFixed(2) || 0.0;
          const value = p.colDef.valueGetter(p);
          return (parseFloat(value) || 0.00).toFixed(2);
        }
      },
      {
        headerName: 'Fee',
        colId: 'Fee',
        filter: 'agTextColumnFilter',
        cellClass: 'currencyStyle',
        valueGetter: (p) => {
          if (!p.data) return '';
          return p.data[1]?.fee;
        },
      },
      {
        headerName: 'Surcharge',
        colId: 'Surcharge',
        filter: 'agTextColumnFilter',
        cellClass: 'currencyStyle',
        valueGetter: (p) => {
          if (!p.data) return '0.00';
          return p.data[1]?.surcharge;
        },
      },
      {
        headerName: 'Platform Fee',
        colId: 'PlatformFee',
        filter: 'agTextColumnFilter',
        cellClass: 'currencyStyle',
        valueGetter: (p) => {
          if (!p.data) return '0.00';
          return p.data[1]?.platform_fee;
        },
      },
    ]
  }]),
  ...(/*store.getters.merchantHasPermissions ? [] : */[{
    headerName: 'Payer',
    children: [
      {
        headerName: 'First Name',
        colId: 'First Name',
        filter: 'agTextColumnFilter',
        valueGetter: (p) => {
          const tx = sourceTx(p?.data?.[1]);
          const meta = getEffectiveMeta(tx) || {};
          return meta.firstName || meta.first_name || '';
        },
      },
      {
        headerName: 'Last Name',
        colId: 'Last Name',
        filter: 'agTextColumnFilter',
        valueGetter: (p) => {
          const tx = sourceTx(p?.data?.[1]);
          const meta = getEffectiveMeta(tx) || {};
          return meta.lastName || meta.last_name || '';
        },
      },
      {
        headerName: 'Email',
        colId: 'Email',
        filter: 'agTextColumnFilter',
        valueGetter: (p) => {
          const tx = sourceTx(p?.data?.[1]);
          const meta = getEffectiveMeta(tx) || {};
          return meta.email || '';
        },
      },
      /*
      {
        headerName: 'Zip Code',
        colId: 'Zip Code',
        filter: 'agTextColumnFilter',
        valueGetter: (p) => {
          if (!p.data) return '';
          return p.data[1]?.data?.api_response?.meta?.zipCode;
        },
      },
      */
    ]
  }]),
  ...columnDefsExtra.value,
  {
    headerName: 'Actions',
    children: [
      {
        headerName: 'Open',
        colId: 'Open',
        filter: false,
        field: 'view',
        cellRenderer: (p) => {
          if (p.data && p.data[1]?.amount)
            return `<p style="font-size: 12px; text-decoration: underline;">Open</p>`;
          return '';
        },
        onCellClicked: function (params) {
          openTransaction(params.node.data);
        }
      },
      {
        headerName: 'Void',
        colId: 'Void',
        filter: false,
        field: 'void',
        hide: !hasPermissions.value || !userHasAnyVoidPermission.value,
        cellRenderer: function (p) {
          const returnLink = `<p style="font-size: 12px; text-decoration: underline;">Void</p>`;
          if (!p.data) return '';
          const txData = p.data[1];

          const shouldShow = shouldShowVoid({
            txData,
            hasPermissions,
            userHasVoidPermission,
            userHasVoidPermission_to_merchants,
            userHasVoidCardPermission,
            userHasVoidCardPermission_to_merchants,
            userHasVoidAchPermission,
            userHasVoidAchPermission_to_merchants
          });

          return shouldShow ? returnLink : '';
        },
        onCellClicked: function (p) {
          if (!p.data) return '';
          const txData = p.data[1];

          const shouldShow = shouldShowVoid({
            txData,
            hasPermissions,
            userHasVoidPermission,
            userHasVoidPermission_to_merchants,
            userHasVoidCardPermission,
            userHasVoidCardPermission_to_merchants,
            userHasVoidAchPermission,
            userHasVoidAchPermission_to_merchants
          });

          if (shouldShow) {
            transactionDetails.value = txData;
            showVoidModal.value = true;
          }
        }
      },
      {
        headerName: 'Refund',
        colId: 'Refund',
        filter: false,
        field: 'refund',
        hide: hasPermissions.value && !userHasAnyRefundPermission.value,
        cellRenderer: function (p) {
          const returnLink = `<p style="font-size: 12px; text-decoration: underline;">Refund</p>`;
          if (!p.data) return '';
          const txData = p.data[1];

          const shouldShow = shouldShowRefund({
            txData,
            hasPermissions,
            userHasAchRefundPermission,
            userHasAchRefundPermission_to_merchants,
            userHasCreditCardRefundPermission,
            userHasCreditCardRefundPermission_to_merchants,
            userHasRefundCardPermission,
            userHasRefundCardPermission_to_merchants,
            userHasRefundAchPermission,
            userHasRefundAchPermission_to_merchants
          });

          return shouldShow ? returnLink : '';
        },
        onCellClicked: function (p) {
          if (!p.data) return '';
          const txData = p.data[1];

          const shouldShow = shouldShowRefund({
            txData,
            hasPermissions,
            userHasAchRefundPermission,
            userHasAchRefundPermission_to_merchants,
            userHasCreditCardRefundPermission,
            userHasCreditCardRefundPermission_to_merchants,
            userHasRefundCardPermission,
            userHasRefundCardPermission_to_merchants,
            userHasRefundAchPermission,
            userHasRefundAchPermission_to_merchants
          });

          if (shouldShow) {
            transactionDetails.value = txData;
            showRefundModal.value = true;
          }
        }
      }
    ]
  },
]);

/**
 * @param {ReportingTuple} transaction
 */
function openTransaction(transaction) {
  transactionDetails.value = transaction[1];
  showViewDetails.value = transaction;
  showViewModal.value = true;
}

function closeTransaction() {
  transactionDetails.value = null;
  showViewDetails.value = false;
  showViewModal.value = false;
  showRefundModal.value = false;
  showVoidModal.value = false;
}
</script>

<style lang="scss" scoped>
[v-cloak] {
  display: none;
}
.loader {
  z-index: 2;
}

.table {
  height: 70vh !important;
}

.subs-btn {
  height: 40px;
  padding: 0;
  background: #fff;
  border-color: #f6951e !important;
  color: #f6951e !important;

  &:hover {
    background: var(--c-grey-light) !important;
  }
}

@media screen and (max-width: 768px) {
  .reporting-filters {
    flex-direction: column;
  }

  .reporting-filters-container {
    flex-direction: column-reverse;
  }

  .report-input {
    width: 50%;

    &:last-child {
      margin-right: 0 !important;
    }
  }
}

::v-deep(.report-buttons) {
  @media screen and (max-width: 768px) {
    .btn {
      width: 50%;
    }
  }
}
</style>

<style lang="scss">
@import '~ag-grid-community/styles/ag-grid.css';
@import '~ag-grid-community/styles/ag-theme-balham.min.css';
.reporting {
  .status-value {
    &.Failed {
      background: #ffe4e4;
      color: #b3322c;
    }

    &.Success {
      color: var(--c-success);
      background: #ddffef;
    }
  }

  .actions {
    height: 100% !important;
  }

  .action-btn {
    line-height: initial;
    width: 30px !important;
    height: 30px !important;
    padding: 2px 1px !important;
    font-size: 14px !important;
    border-radius: 50% !important;

    .icon-container {
      width: auto !important;

      svg {
        width: 19px !important;
      }
    }

    &.success {
      color: #fff !important;
    }
  }
}

/* Full-screen overlay for All Results mode */
.all-results-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.all-results-overlay .overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  font-size: 18px;
  color: #333;
}
.all-results-overlay .overlay-text {
  font-weight: 500;
  margin-top: 8px;
}
.all-results-overlay .overlay-spinner {
  margin-bottom: 8px;
}
</style>
